import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AuthService } from '@auth0/auth0-angular';
import { Observable, of } from 'rxjs';
import { catchError, filter, map, mergeMap, shareReplay, switchMap, take } from 'rxjs/operators';
import {
  AggregationResult,
  ClientDashboardMetadata,
  ClientLayoutItem,
  LoadObjectRequest,
  UploadedFileRef,
} from '@mmlogic/components';
import { ScriptNotification } from '../model/script-notification.model';
import { DataClass } from '../model/data-model/data-class.model';
import { PagedResponse } from '../model/response.model';
import { environment } from 'src/environments/environment';
import { DataObject } from '../model/data-model/data-object.model';
import { AccessVisibility } from '../model/data-model/object-access.model';
import { AppService } from './app.service';
import { Module } from '../model/tenants/module.model';

/** Encodes each class name individually so the comma stays a separator the backend can split on. */
function joinClasses(type: string, objectClass: string | null): string {
  return [type, objectClass].filter((value): value is string => !!value).map(encodeURIComponent).join(',');
}

@Injectable({
  providedIn: 'root'
})
export class ApiClientService {
  metadataCache: Map<string, Observable<ClientDashboardMetadata>> = new Map<string, Observable<ClientDashboardMetadata>>();

  constructor(public auth: AuthService,
    private http: HttpClient,
    private app: AppService) { }

  private getClientDashboardMetadata(uri: string, name: string | null): Observable<ClientDashboardMetadata> {
    return this.app.userProfileSubject.pipe(
      filter(profile => profile !== null),
      take(1),
      switchMap(() => {
        let targetUri = uri;
        targetUri += (targetUri.includes('?') ? '&' : '?');
        targetUri += 'language=' + encodeURIComponent(this.app.getLanguage());
        if (name) {
          targetUri += '&name=' + encodeURIComponent(name);
        }

        let result = this.metadataCache.get(targetUri);
        if (!result) {
          result = this.http
            .get<ClientDashboardMetadata>(targetUri)
            .pipe(shareReplay());
          this.metadataCache.set(targetUri, result);
        }
        return result;
      })
    );
  }

  getNavigationMetadata(tenant: string): Observable<ClientDashboardMetadata> {
    return this.getClientDashboardMetadata(`${environment.endpoints.metadata}/${tenant}/navigationPane`, null);
  }

  getDashboard(tenant: string, name: string | null): Observable<ClientDashboardMetadata> {
    return this.getClientDashboardMetadata(`${environment.endpoints.metadata}/${tenant}/dashboard`, name);
  }

  getClassDashboard(tenant: string, className: string, name: string | null): Observable<ClientDashboardMetadata> {
    return this.getClientDashboardMetadata(`${environment.endpoints.metadata}/${tenant}/dashboard/${className}`, name);
  }

  getClassCreateForm(tenant: string, type: string, objectClass: string | null, name: string | null): Observable<ClientDashboardMetadata> {
    const classes = joinClasses(type, objectClass);
    const nameParam = name ? `&name=${encodeURIComponent(name)}` : '';
    return this.getClientDashboardMetadata(`${environment.endpoints.metadata}/${tenant}/form?classes=${classes}&mode=CREATE${nameParam}`, null);
  }

  getRelatedObjectsForm(tenant: string, referenceClass: string, type: string, objectClass: string | null): Observable<ClientDashboardMetadata> {
    const classes = joinClasses(type, objectClass);
    return this.getClientDashboardMetadata(`${environment.endpoints.metadata}/${tenant}/form?classes=${classes}&referenceClass=${encodeURIComponent(referenceClass)}&mode=CREATE`, null);
  }

  getDashboardByUrl(uri: string): Observable<ClientDashboardMetadata> {
    return this.getClientDashboardMetadata(environment.apiUrl + uri, null);
  }

  /** Answers mrdLoadObject (mrd-layout-section / mrd-form): the object at `request.href`
   *  plus, for a RELATED_OBJECT request, its class's (named) RELATED_OBJECT layout via the
   *  object's `_relatedDataObject` link. Items are passed through in the flat API format.
   *  Never errors: a failed object GET (403/404) yields `data: null`; a missing or failed
   *  layout yields `[]`, which the component renders as a plain relation row. */
  loadObject(request: LoadObjectRequest): Observable<{ data: DataObject | null; layout: ClientLayoutItem[] | null }> {
    return this.getObjectByUrl(request.href).pipe(
      switchMap((data) => {
        if (request.layout?.kind !== 'RELATED_OBJECT') return of({ data, layout: null });
        const metaHref = data?._links?._relatedDataObject?.href;
        if (!metaHref) return of({ data, layout: [] });
        return this.getClientDashboardMetadata(environment.apiUrl + metaHref, request.layout.name ?? null).pipe(
          map((meta) => ({ data, layout: meta?.layouts?.[0]?.items ?? [] })),
          catchError(() => of({ data, layout: [] })),
        );
      }),
      catchError(() => of({ data: null, layout: null })),
    );
  }

  getActiveModules(tenant: string): Observable<Module[]> {
    return this.http.get<Module[]>(`${environment.endpoints.metadata}/${tenant}/modules`);
  }

  getAvailableModules(tenant: string): Observable<Module[]> {
    return this.http.get<Module[]>(`${environment.endpoints.metadata}/${tenant}/modules?available=true`);
  }

  activateModule(tenant: string, module: Module) {
    return this.http.post<Module>(`${environment.endpoints.metadata}/${tenant}/modules`, module);
  }

  getClasses(tenant: string): Observable<DataClass[]> {
    return this.http.get<DataClass[]>(`${environment.endpoints.metadata}/${tenant}/classes`);
  }

  getAllObjects(tenant: string, type: string): Observable<PagedResponse> {
    return this.getObjectsByUrl(`/data/${tenant}/${type}?sort=name&size=1000`);
  }

  getSearchUrl(tenant: string, type: string, filter: string): string {
    return `/data/${tenant}/${type}?q=${encodeURIComponent(filter)}`;
  }

  getGlobalSearchUrl(tenant: string, query: string, classes?: string): string {
    const classesParam = classes ? `&classes=${classes.split(',').map(encodeURIComponent).join(',')}` : '';
    return `/data/${tenant}/search?q=${encodeURIComponent(query)}${classesParam}`;
  }

  getObjectsByUrl(url: string): Observable<PagedResponse> {
    return this.http.get<PagedResponse>(environment.apiUrl + url);
  }

  getAggregationsByUrl(url: string): Observable<AggregationResult> {
    return this.http.get<AggregationResult>(environment.apiUrl + url);
  }

  /** Distinct values (with count) for a field/relation — powers the document folder tree. */
  getDistinctByUrl(url: string): Observable<{ values: { value: unknown; count: number }[] }> {
    return this.http.get<{ values: { value: unknown; count: number }[] }>(environment.apiUrl + url);
  }

  getObjectByUrl(url: string): Observable<DataObject> {
    return this.http.get<DataObject>(environment.apiUrl + url);
  }

  getObject(tenant: string, type: string, id: string): Observable<DataObject> {
    return this.http.get<DataObject>(`${environment.endpoints.data}/${tenant}/${type}/${id}`);
  }

  getObjectOwn(tenant: string, type: string): Observable<DataObject> {
    return this.http.get<DataObject>(`${environment.endpoints.data}/${tenant}/${type}/own`);
  }

  /** Per-object mayModify/mayDelete (TASK-0285) — a dedicated endpoint, not inlined on
   *  the plain object GET. Visibility-gated the same way a mutation is (404 rather than
   *  a silent false when the object isn't visible at all). */
  getObjectCapabilities(tenant: string, type: string, id: string): Observable<{ mayModify: boolean; mayDelete: boolean }> {
    return this.http.get<{ mayModify: boolean; mayDelete: boolean }>(`${environment.endpoints.data}/${tenant}/${type}/${id}/capabilities`);
  }

  /** Whether the current user may create a `type` record for a top-level VIEW/list
   *  (TASK-0285/FINDING-0155). `filterClass` narrows to a subtype when the view is
   *  filtered to one (same value sent as `?class=` on the view's own row fetch).
   *  `allowedClasses` — the concrete classes a create POST would actually accept when `type`
   *  is a family — is absent on an older API that doesn't report it (TASK-0313/TASK-0315). */
  getViewCapabilities(tenant: string, type: string, filterClass?: string | null): Observable<{ mayCreate: boolean; allowedClasses?: string[] }> {
    const filterParam = filterClass ? `?class=${encodeURIComponent(filterClass)}` : '';
    return this.http.get<{ mayCreate: boolean; allowedClasses?: string[] }>(`${environment.endpoints.data}/${tenant}/${type}/capabilities${filterParam}`);
  }

  /** Same question for a RELATED_VIEW ('may I add a `type` to this `refType`/`refId`') —
   *  the parent-conditional form of getViewCapabilities(). */
  getRelatedViewCapabilities(
    tenant: string, refType: string, refId: string, type: string, filterClass?: string | null
  ): Observable<{ mayCreate: boolean; allowedClasses?: string[] }> {
    const filterParam = filterClass ? `?class=${encodeURIComponent(filterClass)}` : '';
    return this.http.get<{ mayCreate: boolean; allowedClasses?: string[] }>(`${environment.endpoints.data}/${tenant}/${refType}/${refId}/${type}/capabilities${filterParam}`);
  }

  /** Admin-only audit view of an object's ReBAC ACL — never dragged in behind the ordinary
   *  object GET, since a caller without a granting definition should still see the record. */
  getAccessVisibility(tenant: string, type: string, id: string): Observable<AccessVisibility> {
    return this.http.get<AccessVisibility>(`${environment.endpoints.audit}/${tenant}/${type}/${id}/access`);
  }

  getDownloadLink(url: string): Observable<string> {
    return this.http.get<string>(environment.apiUrl + url);
  }

  createObject(tenant: string, type: string, value: any): Observable<string> {
    return this.http.post(`${environment.endpoints.data}/${tenant}/${type}`, value, { observe: 'response' })
      .pipe(map(response => response.headers.get('location') || ''));
  }

  updateObject(tenant: string, type: string, id: string, value: any): Observable<unknown> {
    return this.http.patch(`${environment.endpoints.data}/${tenant}/${type}/${id}`, value);
  }

  updateObjectByUrl(url: string, value: any): Observable<unknown> {
    return this.http.patch(environment.apiUrl + url, value);
  }

  mergeObject(winnerHref: string, loserHref: string, patch: Record<string, unknown>): Observable<void> {
    return this.http.post<void>(environment.apiUrl + winnerHref + '/merge', { loser: loserHref, patch });
  }

  createObjectByUrl(url: string, value: any): Observable<string> {
    return this.http.post(environment.apiUrl + url, value, { observe: 'response' })
      .pipe(map(response => response.headers.get('location') || ''));
  }

  createReferencedObject(tenant: string, referenceClass: string, id: string, type: string, objectClass: string | null, value: any): Observable<string> {
    const filter = objectClass ? '?class=' + encodeURIComponent(objectClass) : '';
    return this.http.post(`${environment.endpoints.data}/${tenant}/${referenceClass}/${id}/${type}${filter}`, value, { observe: 'response' })
      .pipe(map(response => response.headers.get('location') || ''));
  }

  /** The upload endpoint returns the server's own determination of fileName/
   *  contentType/size alongside the href — that metadata must be echoed back
   *  unchanged when the FILE field is claimed on create/patch, since the
   *  server verifies it against what it recorded here (TASK-0351). */
  upload(tenant: string, file: File): Observable<UploadedFileRef> {
    const formData = new FormData();
    formData.append("file", file);

    return this.http
      .get<string>(`${environment.endpoints.data}/${tenant}/upload`)
      .pipe(
        mergeMap(url => this.http.post<UploadedFileRef[]>(url, formData)),
        map(results => results[0])
      )
  }

  getReportLink(url: string): Observable<string> {
    return this.http.get<string>(environment.apiUrl + url);
  }

  deleteObject(url: string): Observable<void> {
    return this.http.delete<void>(environment.apiUrl + url);
  }

  getReport(url: string): Observable<string> {
    return this.http.get<string>(environment.apiUrl + url);
  }

  doAction(uri: string | undefined, actionName: string): Observable<ScriptNotification | null> {
    return this.http.post<ScriptNotification>(
      environment.apiUrl + uri + '/' + actionName, {},
      { observe: 'response' }
    ).pipe(
      map(response => response.status === 200 ? response.body : null)
    );
  }

  importFile(tenant: string, value: any): Observable<string> {
    return this.http
      .post(`${environment.endpoints.data}/${tenant}/import`, value, { observe: 'response' })
      .pipe(map((response) => response.headers.get('location') || ''));
  }

  // The system model is shared across tenants, but its client metadata is published per tenant:
  // some fields carry options that only the tenant's own model knows (e.g. the access roles on a
  // user profile), so every systemMetadata call passes the tenant.
  getSystemForm(tenant: string, type: string): Observable<ClientDashboardMetadata> {
    return this.http.get<ClientDashboardMetadata>(
      `${environment.endpoints.systemMetadata}/${tenant}/form?classes=${encodeURIComponent(type)}`
    );
  }

  acceptInvitation(tenantId: string, subject: string, token: string): Observable<void> {
    return this.http.get<void>(
      `${environment.apiUrl}/signup/${encodeURIComponent(tenantId)}/${encodeURIComponent(subject)}/${encodeURIComponent(token)}`
    );
  }

  getSystemClassDashboard(tenant: string, type: string): Observable<ClientDashboardMetadata> {
    return this.getClientDashboardMetadata(
      `${environment.endpoints.systemMetadata}/${tenant}/dashboard/${type}`,
      null
    );
  }

  getSystemClassCreateForm(tenant: string, type: string, objectClass: string | null): Observable<ClientDashboardMetadata> {
    const classes = joinClasses(type, objectClass);
    return this.getClientDashboardMetadata(
      `${environment.endpoints.systemMetadata}/${tenant}/form?classes=${classes}&mode=CREATE`,
      null
    );
  }

  getSystemRelatedObjectsForm(tenant: string, referenceClass: string, type: string, objectClass: string | null): Observable<ClientDashboardMetadata> {
    const classes = joinClasses(type, objectClass);
    return this.getClientDashboardMetadata(
      `${environment.endpoints.systemMetadata}/${tenant}/form?classes=${classes}&referenceClass=${encodeURIComponent(referenceClass)}&mode=CREATE`,
      null
    );
  }

  getSystemObject(tenant: string, type: string, id: string): Observable<DataObject> {
    return this.http.get<DataObject>(`${environment.apiUrl}/system/${tenant}/${type}/${id}`);
  }

  createSystemObject(tenant: string, type: string, value: any): Observable<string> {
    return this.http.post(`${environment.apiUrl}/system/${tenant}/${type}`, value, { observe: 'response' })
      .pipe(map(response => response.headers.get('location') || ''));
  }

  createReferencedSystemObject(tenant: string, referenceClass: string, id: string, type: string, value: any): Observable<string> {
    return this.http.post(`${environment.apiUrl}/system/${tenant}/${referenceClass}/${id}/${type}`, value, { observe: 'response' })
      .pipe(map(response => response.headers.get('location') || ''));
  }

  getSystemSearchUrl(tenant: string, type: string, filter: string): string {
    return `/system/${tenant}/${type}?q=${encodeURIComponent(filter)}`;
  }

  getActuatorHealth(): Observable<{ status: string }> {
    return this.http.get<{ status: string }>(`${environment.endpoints.actuator}/health`);
  }

  getQueueStatus(): Observable<Record<string, { queueDepth: number; inFlightCount: number; dlqCount: number }>> {
    return this.http.get<Record<string, { queueDepth: number; inFlightCount: number; dlqCount: number }>>(`${environment.endpoints.actuator}/queues`);
  }

  getBufferedGatewayStatus(): Observable<Record<string, { totalBuffered: number; activeGroups: number; oldestEventAgeSeconds: number; lastFlushAt: string | null; totalFlushErrors: number }>> {
    return this.http.get<Record<string, { totalBuffered: number; activeGroups: number; oldestEventAgeSeconds: number; lastFlushAt: string | null; totalFlushErrors: number }>>(`${environment.endpoints.actuator}/bufferedgateways`);
  }

  getDbPoolStatus(): Observable<{ acquired: number; allocated: number; idle: number; pending: number; maxAllocated: number; maxPending: number } | null> {
    return this.http.get<{ acquired: number; allocated: number; idle: number; pending: number; maxAllocated: number; maxPending: number } | null>(`${environment.endpoints.actuator}/dbpool`);
  }
}
